import React, { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";

import { CreditCard, Lock } from "lucide-react";
import { CardElement, useElements } from "@stripe/react-stripe-js";
import  usePaymentMethod  from "@/hooks/stripe/usePaymentMethod";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

type PaymentMethodProps = {
  existingStripePaymentMethods?: any[];
  onRemoveStripePaymentMethod?: (paymentMethodId: string) => void;
  handlePaymentMethodSubmit: () => void;
  loading?: boolean;
  existingStripePaymentMethodsLoading?: boolean;
};

const HandlePaymentMethods = React.forwardRef<any, PaymentMethodProps>(
  (
    {
      existingStripePaymentMethods,
      onRemoveStripePaymentMethod,
      handlePaymentMethodSubmit,
      loading,
      existingStripePaymentMethodsLoading,
    },
    ref
  ) => {
    const elements = useElements();
    const { createPaymentMethod, error } = usePaymentMethod();
    const [cardError, setCardError] = useState<string | null>(null);
    const [cardKey, setCardKey] = useState(0);
    const isCardExpired = (expMonth: number, expYear: number) => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (expYear < currentYear) return true;
      if (expYear === currentYear && expMonth < currentMonth) return true;

      return false;
    };

    const cardElementOptions = {
      style: {
        base: {
          fontSize: "16px",
          color: "#424770",
          "::placeholder": {
            color: "#aab7c4",
          },
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSmoothing: "antialiased",
        },
        invalid: {
          color: "#9e2146",
        },
      },
      hidePostalCode: true,
    };

    const handleCreatePaymentMethod = async (billingDetails: any) => {
      setCardError(null);

      const cardElement = elements?.getElement(CardElement);

      if (!cardElement) {
        const errorMsg = "Card element not found";
        setCardError(errorMsg);
        toast.error(errorMsg);
        return null;
      }

      try {
        const paymentMethodData = {
          type: "card" as const,
          card: cardElement,
          billing_details: billingDetails,
        };

        const result = await createPaymentMethod(paymentMethodData);

        if (result) {
          setCardKey((prev) => prev + 1);
          return result;
        } else {
          const errorMsg = "Failed to create payment method";
          setCardError(errorMsg);
          toast.error(errorMsg);
          return null;
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Unknown payment error";
        setCardError(errorMsg);
        toast.error("Payment method creation failed", {
          description: errorMsg,
        });
        return null;
      }
    };

    React.useImperativeHandle(ref, () => ({
      createPaymentMethod: handleCreatePaymentMethod,
    }));

    return (
      <Card className="border shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>Select your preferred payment method</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-10  pt-6 border-t">
            <div className="space-y-8">
              <Label>Saved Cards</Label>

              {/* Existing payment methods */}

              <div className="space-y-3">
                {existingStripePaymentMethodsLoading ? (
                  <p className="text-base text-gray-700 text-center">
                    Loading existing payment methods...
                  </p>
                ) : !existingStripePaymentMethods || existingStripePaymentMethods?.length === 0 ? (
                  <p className="text-sm text-gray-500">No saved payment methods found.</p>
                ) : (
                  existingStripePaymentMethods?.map((paymentMethod: any) => {
                    const isExpired = isCardExpired(
                      paymentMethod.card?.exp_month,
                      paymentMethod.card?.exp_year
                    );

                    return (
                      <div
                        key={paymentMethod.id}
                        className={`rounded-md border p-4 flex justify-between items-center ${
                          isExpired ? "bg-red-50 border-red-200" : "bg-white border-gray-200"
                        }`}
                      >
                        <div>
                          <div className="font-medium">
                            {paymentMethod?.card?.brand?.toUpperCase()} ••••{" "}
                            {paymentMethod?.card?.last4}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Expires {paymentMethod?.card?.exp_month}/{paymentMethod?.card?.exp_year}
                            {isExpired && <span className="text-red-500 ml-2">(Expired)</span>}
                          </div>
                        </div>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size={"sm"}
                              className="text-white bg-red-600 hover:bg-red-500 rounded-md hover:text-white "
                            >
                              Remove
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove card?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This card will be permanently removed.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700 rounded-md"
                                onClick={() => onRemoveStripePaymentMethod?.(paymentMethod.id)}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <div className="space-y-2">
                <Label>Add New Card</Label>
                <div
                  className={`border rounded-md p-3 bg-white ${cardError ? "border-red-500" : ""}`}
                >
                  <CardElement options={cardElementOptions}  key={cardKey}/>
                </div>
                {(error || cardError) && (
                  <p className="text-sm text-red-500">{error || cardError}</p>
                )}
                {loading && <p className="text-sm text-blue-500">Processing payment method...</p>}
              </div>
              <div className="flex items-center text-sm text-gray-500 mt-2">
                <Lock className="h-3 w-3 mr-1" />
                Your payment information is secured with SSL encryption
              </div>
              <div className="pt-6">
                <Button
                  onClick={handlePaymentMethodSubmit}
                  className="w-full  bg-black hover:bg-black/90 text-white h-11 rounded-md hover:text-white "
                  variant={"outline"}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Save Card"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

HandlePaymentMethods.displayName = "HandlePaymentMethods";

export default HandlePaymentMethods;
